// components/dashboard/TradeStats.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../../services/firebase';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { useAuthContext } from '../../../contexts/AuthContext';

const devLog = (...args) => {
  if (import.meta.env.DEV) console.log(...args);
};

const formatPnl = (value) => {
  const abs = Math.abs(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (value > 0) return `+$${abs}`;
  if (value < 0) return `-$${abs}`;
  return `$${abs}`;
};

const TradeStats = () => {
  const { user } = useAuthContext();
  const [totalTrades, setTotalTrades] = useState(0);
  const [realizedPnl, setRealizedPnl] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const userId = user?.uid;

  useEffect(() => {
    const fetchStats = async () => {
      if (!userId) {
        devLog('[TradeStats] skip fetch: no userId');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        devLog('[TradeStats] fetching stats');

        const backlogsQuery = query(
          collection(db, 'tbl_user_order_backlogs'),
          where('userId', '==', userId),
          where('tradeType', '==', 'completed'),
          limit(2000)
        );

        const backlogsSnapshot = await getDocs(backlogsQuery);

        let completedCount = 0;
        backlogsSnapshot.forEach((doc) => {
          const data = doc.data();
          const isCompleted =
            data.tradeType === 'completed' ||
            data.lifecycle?.phase === 'completed' ||
            (data.lifecycle?.exit && data.lifecycle?.entry);

          if (isCompleted) {
            completedCount++;
          }
        });

        setTotalTrades(completedCount);

        const dailyPnlQuery = query(
          collection(db, 'tbl_user_daily_pnl'),
          where('userId', '==', userId),
          limit(2000)
        );

        const dailyPnlSnapshot = await getDocs(dailyPnlQuery);

        let totalRealizedPnl = 0;
        dailyPnlSnapshot.forEach((doc) => {
          const data = doc.data();
          // totalNetPnl is stored in DOLLARS since #210 restated the aggregates
          // (backlog performance now carries the contract multiplier at write time).
          // The ×100 added in #197 matched the old premium-points storage; keeping
          // it against restated data would overstate every figure 100×.
          const netPnl = data.totalNetPnl || 0;
          totalRealizedPnl += netPnl;
        });

        setRealizedPnl(totalRealizedPnl);
        setError(null);
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError('Failed to load trade statistics. Please try refreshing the page.');
        setTotalTrades(0);
        setRealizedPnl(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [userId]);

  const pnlColor =
    realizedPnl > 0 ? 'text-emerald-400' : realizedPnl < 0 ? 'text-red-400' : 'text-gray-300';

  return (
    <div className="mt-4 pt-4 border-t border-purple-900/40">
      {error && (
        <div className="mb-3 p-2 bg-red-900/30 border border-red-700 rounded text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <div>
            <span className="text-gray-400">Realized P&L: </span>
            <span className={`font-semibold ${pnlColor}`}>
              {isLoading ? '...' : formatPnl(realizedPnl)}
            </span>
          </div>
          <div>
            <span className="text-gray-400">Total trades: </span>
            <span className="font-semibold text-white">
              {isLoading ? '...' : totalTrades.toLocaleString()}
            </span>
          </div>
        </div>

        <Link
          to="/trade-history"
          className="text-purple-400 hover:text-purple-300 text-sm font-medium transition-colors whitespace-nowrap"
        >
          View full trade history
        </Link>
      </div>
    </div>
  );
};

export default TradeStats;
